<script setup lang="ts">
import ProjectSharePointCell from '@/components/admin/project/ProjectSharePointCell.vue';
import { models } from 'cesg-shared';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { BulkProvisioningPhase } from './use-bulk-provisioning';

const { t } = useI18n();

const props = defineProps<{
  pendingCount: number;
  isRunning: boolean;
  isStopping: boolean;
  phase: BulkProvisioningPhase;
  total: number;
  settled: number;
  readyCount: number;
  failedProjects: models.ListAdminProjectsResponse;
  /** D17 hand-backs: still `IN_PROGRESS` server-side, so neither ready nor failed. */
  deferredProjects: models.ListAdminProjectsResponse;
  showSummary: boolean;
}>();

defineEmits<{
  start: [];
  stop: [];
  provision: [projectId: string];
  dismiss: [];
}>();

/** `"Etablerar 34 / 212 — 3 misslyckade, 2 väntar"` — the tail segments only when non-zero. */
const progressLabel = computed(() => {
  const head = t('adminProjects.sharePointBulkProgress', {
    settled: props.settled,
    total: props.total,
  });

  const segments: string[] = [];
  if (props.failedProjects.length > 0) {
    segments.push(
      t('adminProjects.sharePointBulkFailedCount', {
        count: props.failedProjects.length,
      }),
    );
  }
  if (props.deferredProjects.length > 0) {
    segments.push(
      t('adminProjects.sharePointBulkDeferredCount', {
        count: props.deferredProjects.length,
      }),
    );
  }

  return segments.length > 0 ? `${head} — ${segments.join(', ')}` : head;
});
</script>

<template>
  <div class="bulk-provisioning">
    <button
      v-if="!isRunning"
      class="button is-primary is-small bulk-start"
      :disabled="pendingCount === 0"
      @click="$emit('start')"
    >
      {{ t('adminProjects.sharePointBulkStart', { count: pendingCount }) }}
    </button>

    <template v-else>
      <span class="bulk-progress">{{ progressLabel }}</span>
      <!-- The D17 idle tail can be minutes long; saying so keeps it from reading as a hang. -->
      <span v-if="phase === 'waiting-for-stale'" class="is-size-7 bulk-waiting">
        {{ t('adminProjects.sharePointBulkWaiting') }}
      </span>
      <button
        class="button is-small bulk-stop"
        :disabled="isStopping"
        @click="$emit('stop')"
      >
        {{
          isStopping
            ? t('adminProjects.sharePointBulkStopping')
            : t('adminProjects.sharePointBulkStop')
        }}
      </button>
    </template>

    <div v-if="showSummary" class="bulk-summary">
      <div class="bulk-summary-head">
        <span>
          {{
            t('adminProjects.sharePointBulkDone', {
              ready: readyCount,
              total: total,
            })
          }}
        </span>
        <button class="button is-small bulk-dismiss" @click="$emit('dismiss')">
          {{ t('adminProjects.sharePointBulkDismiss') }}
        </button>
      </div>

      <!-- Each row reuses ProjectSharePointCell, so the chip, the ledger's `lastError` text and an
           individually-retryable Retry button all come for free. -->
      <template v-if="failedProjects.length > 0">
        <p class="bulk-group-title">
          {{ t('adminProjects.sharePointBulkFailedHeading') }}
        </p>
        <div
          v-for="project in failedProjects"
          :key="project.id"
          class="bulk-summary-row bulk-failed-row"
        >
          <span>{{ project.fortnoxId }} - {{ project.name }}</span>
          <ProjectSharePointCell
            :share-point="project.sharePoint"
            @provision="$emit('provision', project.id)"
          />
        </div>
      </template>

      <template v-if="deferredProjects.length > 0">
        <p class="bulk-group-title">
          {{ t('adminProjects.sharePointBulkDeferredHeading') }}
        </p>
        <div
          v-for="project in deferredProjects"
          :key="project.id"
          class="bulk-summary-row bulk-deferred-row"
        >
          <span>{{ project.fortnoxId }} - {{ project.name }}</span>
          <ProjectSharePointCell
            :share-point="project.sharePoint"
            @provision="$emit('provision', project.id)"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.bulk-provisioning {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 0.75rem;
}

.bulk-progress {
  font-size: 0.875rem;
}

.bulk-waiting {
  opacity: 0.75;
}

.bulk-summary {
  flex-basis: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #c0d4f7;
  border-radius: 6px;
}

.bulk-summary-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-weight: 600;
}

.bulk-group-title {
  margin-top: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
}

.bulk-summary-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 0.875rem;
}
</style>
